import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 30000 });

// Attach admin JWT if present
client.interceptors.request.use((config) => {
    const url = config.url || "";
    if (url.startsWith("/admin") || url.startsWith("/marketing")) {
        const token = localStorage.getItem("md_admin_token");
        if (token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Redirect to admin login on 401 for admin/marketing routes
client.interceptors.response.use(
    (r) => r,
    (err) => {
        const url = err?.config?.url || "";
        if (
            err?.response?.status === 401 &&
            (url.startsWith("/admin") || url.startsWith("/marketing")) &&
            !window.location.pathname.endsWith("/admin/login")
        ) {
            localStorage.removeItem("md_admin_token");
            window.location.href = "/admin/login";
        }
        return Promise.reject(err);
    },
);

export default client;

// -------- Public API --------
export const fetchNewsFeed = (params = {}) => client.get("/news-feed", { params }).then((r) => r.data);
export const fetchPublicAds = () => client.get("/ads").then((r) => r.data);
export const fetchPublicSettings = () => client.get("/settings").then((r) => r.data);
export const fetchGlobalMarkets = () => client.get("/global-markets").then((r) => r.data);
export const trackImpression = (adId) => client.post(`/ads/${adId}/impression`).catch(() => {});
export const trackClick = (adId) => client.post(`/ads/${adId}/click`).catch(() => {});

// -------- Admin API --------
// Password auth
export const adminLogin = (email, password) =>
    client.post("/admin/login", { email, password }).then((r) => r.data);
export const adminForgotPassword = (email) =>
    client.post("/admin/forgot-password", { email }).then((r) => r.data);
export const adminResetPassword = (token, new_password) =>
    client.post("/admin/reset-password", { token, new_password }).then((r) => r.data);
export const adminChangePassword = (current_password, new_password) =>
    client.post("/admin/change-password", { current_password, new_password }).then((r) => r.data);
export const adminMe = () => client.get("/admin/me").then((r) => r.data);
export const adminStats = () => client.get("/admin/stats").then((r) => r.data);
// Self-service change of the login email (requires current password).
export const adminChangeEmail = (new_email, password) =>
    client.post("/admin/change-email", { new_email, password }).then((r) => r.data);
export const adminProviderStatus = (p) => client.get(`/admin/news-providers/${p}/status`).then((r) => r.data);
export const adminProviderToggle = (p, enabled) => client.patch(`/admin/news-providers/${p}`, { enabled }).then((r) => r.data);
export const adminProviderRun = (p) => client.post(`/admin/news-providers/${p}/run`).then((r) => r.data);
export const adminFeedSettings = () => client.get("/admin/feed/settings").then((r) => r.data);
export const adminSetFeedSettings = (body) => client.patch("/admin/feed/settings", body).then((r) => r.data);
export const adminFeedKeys = () => client.get("/admin/feed/keys").then((r) => r.data);
export const adminCreateFeedKey = (name) => client.post("/admin/feed/keys", { name }).then((r) => r.data);
export const adminToggleFeedKey = (id, enabled) => client.patch(`/admin/feed/keys/${id}`, { enabled }).then((r) => r.data);
export const adminDeleteFeedKey = (id) => client.delete(`/admin/feed/keys/${id}`).then((r) => r.data);
export const adminListSources = () => client.get("/admin/sources").then((r) => r.data);
export const adminCreateSource = (body) => client.post("/admin/sources", body).then((r) => r.data);
export const adminUpdateSource = (id, body) => client.patch(`/admin/sources/${id}`, body).then((r) => r.data);
export const adminDeleteSource = (id) => client.delete(`/admin/sources/${id}`).then((r) => r.data);
export const adminTestSource = (id) => client.post(`/admin/sources/${id}/test`).then((r) => r.data);
export const adminListAds = () => client.get("/admin/ads").then((r) => r.data);
export const adminCreateAd = (body) => client.post("/admin/ads", body).then((r) => r.data);
export const adminUpdateAd = (id, body) => client.patch(`/admin/ads/${id}`, body).then((r) => r.data);
export const adminDeleteAd = (id) => client.delete(`/admin/ads/${id}`).then((r) => r.data);
// Telegram: two channels — "india" and "us" (see backend/telegram_service.py::CHANNEL_IDS).
export const adminTelegramAll = () => client.get("/admin/telegram").then((r) => r.data);
export const adminTelegram = (channelId) => client.get(`/admin/telegram/${channelId}`).then((r) => r.data);
export const adminUpdateTelegram = (channelId, body) => client.put(`/admin/telegram/${channelId}`, body).then((r) => r.data);
export const adminTelegramTest = (channelId) => client.post(`/admin/telegram/${channelId}/test`).then((r) => r.data);
export const adminTelegramHistory = (channelId) => client.get(`/admin/telegram/${channelId}/history`).then((r) => r.data);
export const adminClearTelegramHistory = (channelId) => client.delete(`/admin/telegram/${channelId}/history`).then((r) => r.data);
export const adminGetSettings = () => client.get("/admin/settings").then((r) => r.data);
export const adminUpdateSettings = (body) => client.put("/admin/settings", body).then((r) => r.data);
export const adminUploadFavicon = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/admin/upload/favicon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
};
export const adminClearFavicon = () => client.delete("/admin/upload/favicon").then((r) => r.data);
export const adminUploadLogo = (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/admin/upload/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
};
export const adminClearLogo = () => client.delete("/admin/upload/logo").then((r) => r.data);

// -------- Users (super-admin only) --------
export const adminListUsers   = () => client.get("/admin/users").then((r) => r.data);
export const adminCreateUser  = (body) => client.post("/admin/users", body).then((r) => r.data);
export const adminUpdateUser  = (id, body) => client.patch(`/admin/users/${id}`, body).then((r) => r.data);
export const adminDeleteUser  = (id) => client.delete(`/admin/users/${id}`).then((r) => r.data);

// -------- Marketing API (marketing + super) --------
export const mktStats             = () => client.get("/marketing/stats").then((r) => r.data);
// SEO
export const mktListSEO           = () => client.get("/marketing/seo-pages").then((r) => r.data);
export const mktCreateSEO         = (body) => client.post("/marketing/seo-pages", body).then((r) => r.data);
export const mktUpdateSEO         = (id, body) => client.patch(`/marketing/seo-pages/${id}`, body).then((r) => r.data);
export const mktDeleteSEO         = (id) => client.delete(`/marketing/seo-pages/${id}`).then((r) => r.data);
// Blog
export const mktListBlog          = () => client.get("/marketing/blog").then((r) => r.data);
export const mktGetBlog           = (id) => client.get(`/marketing/blog/${id}`).then((r) => r.data);
export const mktCreateBlog        = (body) => client.post("/marketing/blog", body).then((r) => r.data);
export const mktUpdateBlog        = (id, body) => client.patch(`/marketing/blog/${id}`, body).then((r) => r.data);
export const mktDeleteBlog        = (id) => client.delete(`/marketing/blog/${id}`).then((r) => r.data);
// Promo Banner
export const mktGetPromo          = () => client.get("/marketing/promo-banner").then((r) => r.data);
export const mktUpdatePromo       = (body) => client.put("/marketing/promo-banner", body).then((r) => r.data);
// Redirects
export const mktListRedirects     = () => client.get("/marketing/redirects").then((r) => r.data);
export const mktCreateRedirect    = (body) => client.post("/marketing/redirects", body).then((r) => r.data);
export const mktUpdateRedirect    = (id, body) => client.patch(`/marketing/redirects/${id}`, body).then((r) => r.data);
export const mktDeleteRedirect    = (id) => client.delete(`/marketing/redirects/${id}`).then((r) => r.data);
// Newsletter
export const mktListNewsletter    = () => client.get("/marketing/newsletter").then((r) => r.data);
export const mktDeleteNewsletter  = (id) => client.delete(`/marketing/newsletter/${id}`).then((r) => r.data);
// Robots
export const mktGetRobots         = () => client.get("/marketing/robots").then((r) => r.data);
export const mktUpdateRobots      = (content) => client.put("/marketing/robots", { content }).then((r) => r.data);

// Marketing-editable subset of site settings (home meta, OG/Twitter card, social links, sitemap XML)
export const mktGetSiteSettings   = () => client.get("/marketing/site-settings").then((r) => r.data);
export const mktUpdateSiteSettings = (body) => client.put("/marketing/site-settings", body).then((r) => r.data);

// News archive (tagged historical news for stock-name search)
export const mktListNewsArchive   = (params = {}) => client.get("/marketing/news-archive", { params }).then((r) => r.data);
export const mktNewsArchiveCounts = (params = {}) => client.get("/marketing/news-archive/counts", { params }).then((r) => r.data);
export const mktSymbolSentiment   = (params = {}) => client.get("/marketing/symbol-sentiment", { params }).then((r) => r.data);
export const mktSymbolLeaderboard = (params = {}) => client.get("/marketing/symbol-sentiment/leaderboard", { params }).then((r) => r.data);
export const fetchStockSuggest   = (q) => client.get("/stocks/suggest", { params: { q, limit: 8 } }).then((r) => r.data);
export const fetchLatestReport   = (period) => client.get("/reports/latest", { params: { period } }).then((r) => r.data);
export const mktAutoReports       = (params = {}) => client.get("/marketing/auto-reports", { params }).then((r) => r.data);
export const mktAutoReport        = (id) => client.get(`/marketing/auto-reports/${id}`).then((r) => r.data);
export const mktGenerateAutoReport = (period) => client.post("/marketing/auto-reports/generate", null, { params: { period } }).then((r) => r.data);
export const mktReportPicks       = (params = {}) => client.get("/marketing/report-picks", { params }).then((r) => r.data);
export const mktTestWeeklyDigest  = () => client.post("/marketing/newsletter/weekly-digest/test").then((r) => r.data);
export const mktDownloadNewsArchiveCSV = async (params = {}) => {
    const res = await client.get("/marketing/news-archive/export", { params, responseType: "blob" });
    // Extract filename from Content-Disposition header
    const cd = res.headers?.["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(cd);
    const filename = match ? match[1] : `niftynews-archive.csv`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

// -------- Public marketing endpoints --------
export const fetchPromoBanner     = () => client.get("/promo-banner").then((r) => r.data);
export const fetchBlogList        = (params = {}) => client.get("/blog", { params }).then((r) => r.data);
export const fetchBlogPost        = (slug) => client.get(`/blog/${slug}`).then((r) => r.data);
export const fetchPageSEO         = (path) => client.get("/seo", { params: { path } }).then((r) => r.data);
export const fetchTaggedNews      = (params = {}) => client.get("/tagged-news", { params }).then((r) => r.data);
export const fetchPublicRedirects = () => client.get("/redirects").then((r) => r.data);
export const subscribeNewsletter  = (email, source = "footer") =>
    client.post("/newsletter/subscribe", { email, source }).then((r) => r.data);

// -------- Auth helpers --------
export function getCurrentUser() {
    try { return JSON.parse(localStorage.getItem("md_admin_user") || "null"); }
    catch { return null; }
}
export function getRole() { return getCurrentUser()?.role || "super"; }

// -------- Utils --------
export function timeAgo(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    let diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 0) diff = 0;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

export function isLoggedIn() {
    return !!localStorage.getItem("md_admin_token");
}

export function adminLogout() {
    localStorage.removeItem("md_admin_token");
    localStorage.removeItem("md_admin_user");
}
